import React, { useEffect, useState } from 'react'
import translate from '../../../core/translate'
import styles from './updateQueue.jcss'
import Checkbox from '../../../blocks.simple/checkbox/checkbox'
import { cn } from 'ethcss'
import grid from '../../../blocks.simple/grid/grid.jcss'
import Icon from '../../../blocks.simple/icon/icon'
import { colors } from '../../../theme'
import { api } from '../../../core/api/ConnectionManager'
import moment from 'moment/moment'

export interface IQueue {
    id: number
    name: string
    progress: string
    status: string
    updateType: string
    date: string
    message: string
    code?: string | number | null
    digitalSignages: DigitalSignage[]
}

export type DigitalSignage = {
    id: number
    name: string
    status: string
    lastScreenshot: string
    lastScreenshotDate: string
    task: {
        id: number
        progress: number
        status: string
        date: string
        message?: string | null
        code?: string | number | null
    }
}

const mockQueue = [
    {
        id: 1,
        name: 'Новая очередь - 1',
        progress: '3/50000',
        status: 'in_progress',
        updateType: 'client_app',
        date: '01.06.2025 12:24:00',
        message: 'test test',
        digitalSignages: [
            {
                id: 1,
                name: 'Устройство Android',
                status: 'online',
                lastScreenshot:
                    'https://357538af-4e62bf02-c435-494b-bd13-9d3dbaab16c6-develop-api.s3.timeweb.cloud/8/screenshots/3ac2930202ac0558949c.jpg',
                lastScreenshotDate: '2025-01-09T10:02:36.000Z',
                task: {
                    id: 2,
                    progress: 30,
                    status: 'pending',
                    date: '01.06.2025 12:24:00',
                    message: '',
                },
            },
        ],
    },
    {
        id: 2,
        name: 'Новая очередь - 2',
        progress: '45/50000',
        status: 'canceled',
        updateType: 'client_app',
        date: '01.06.2025 12:24:00',
        message: 'test test',
        digitalSignages: [
            {
                id: 5,
                name: 'Устройство Android',
                status: 'online',
                lastScreenshot:
                    'https://357538af-4e62bf02-c435-494b-bd13-9d3dbaab16c6-develop-api.s3.timeweb.cloud/8/screenshots/5700a75fc7d64127b238.jpg',
                lastScreenshotDate: '2025-01-09T10:02:36.000Z',
                task: {
                    id: 6,
                    progress: 30,
                    status: 'pending',
                    date: '01.06.2025 12:24:00',
                    message: '',
                },
            },
        ],
    },
]

export const UpdateQueue = ({
    selectedQueue,
    onQueueSelect,
    onSelectAll,
    selectedTask,
    onSelectTask,
    onCancelSelect,
}: {
    selectedQueue: IQueue[]
    selectedTask: DigitalSignage | null
    onQueueSelect: (queue: IQueue) => void
    onSelectAll: (queueList: IQueue[]) => void
    onSelectTask: (device: DigitalSignage) => void
    onCancelSelect: () => void
}) => {
    const [queueOpenedList, setQueueOpenedList] = useState<number[]>([])
    const [queueList, setQueueList] = useState<IQueue[]>([])

    const onToggleQueue = (id: number) => {
        if (queueOpenedList.includes(id)) {
            setQueueOpenedList(queueOpenedList.filter((queue) => queue !== id))

            return
        }

        setQueueOpenedList([...queueOpenedList, id])
    }

    const getStatusName = (status: string) => {
        switch (status) {
            case 'pending':
                return translate('inPending')
            case 'canceled':
                return translate('canceled')
            case 'in_progress':
                return translate('inProgress')
            case 'success':
                return translate('updateCompleted')
            case 'error':
                return translate('error')
            case 'warning':
                return translate('warning')
            default:
                return ''
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'created':
            case 'pending':
                return colors.black
            case 'in_progress':
                return colors.warning
            case 'success':
                return colors.success
            case 'error':
            case 'canceled':
            case 'warning':
                return colors.error
            default:
                return colors.black
        }
    }

    const getLocalizedMessage = (code?: string | number | null) => {
        if (code === null || code === undefined) {
            return ''
        }

        const key = String(code).trim()
        const translated = translate(key)

        if (translated !== key) {
            return translated
        }

        return translate('100004')
    }

    useEffect(() => {
        api.send('getDSUpdateQueues', {})
    }, [])

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver(
            'dSUpdateQueuesReceived',
            (queues: any) => {
                if (queues) {
                    setQueueList(queues)
                }
            },
            listenersId
        )

        return () => listenersId.forEach((id) => api.removeObserver(id))
    }, [])

    return (
        <div>
            <table className={styles.table}>
                <tbody>
                    <tr>
                        <th className={cn(styles.th, styles.tdCheckbox)}>
                            <Checkbox
                                checked={selectedQueue.length === queueList.length && !!queueList.length}
                                onClick={() => {
                                    onCancelSelect()

                                    if (selectedQueue.length === queueList.length) {
                                        onSelectAll([])

                                        return
                                    }

                                    onSelectAll(queueList.map((queue) => queue))
                                }}
                            />
                        </th>
                        <th className={cn(styles.th, styles.tdArrow)} />
                        <th className={cn(styles.th, styles.tdStatus)}>{translate('status')}</th>
                        <th className={cn(styles.th, styles.tdProgress)}>{translate('progress')}</th>
                        <th className={cn(styles.th, styles.tdName)}>{translate('name')}</th>
                        <th className={cn(styles.th, styles.tdUpdateType)}>{translate('updateType')}</th>
                        <th className={cn(styles.th, styles.tdDate)}>{translate('date_update')}</th>
                        <th className={cn(styles.th, styles.tdMessage)}>{translate('message')}</th>
                    </tr>
                    {queueList.map((queue) => {
                        const list = []
                        const opened = queueOpenedList.includes(queue.id)

                        list.push(
                            <tr key={queue.id}>
                                <td className={cn(styles.td, styles.tdCheckbox)}>
                                    <Checkbox
                                        checked={selectedQueue.some((item) => item.id === queue.id)}
                                        onClick={() => {
                                            onQueueSelect(queue)
                                            onCancelSelect()
                                        }}
                                    />
                                </td>
                                <td className={cn(styles.td, styles.tdArrow)}>
                                    {queue.digitalSignages && !!queue.digitalSignages.length && (
                                        <Icon
                                            type={opened ? 'arrow_up' : 'arrow_down'}
                                            size={16}
                                            onClick={() => onToggleQueue(queue.id)}
                                        />
                                    )}
                                </td>
                                <td className={styles.td}>{getStatusName(queue.status)}</td>
                                <td className={styles.td}>{queue.progress}</td>
                                <td className={styles.td}>{queue.name}</td>
                                <td className={styles.td}>{translate(queue.updateType)}</td>
                                <td className={styles.td}>{moment(queue.date).format('HH:mm:ss DD.MM.YYYY')}</td>
                                <td className={styles.td}>{getLocalizedMessage(queue.code)}</td>
                            </tr>
                        )
                        if (queue.digitalSignages && queue.digitalSignages.length) {
                            queue.digitalSignages.forEach((device) => {
                                const color = getStatusColor(device.task.status)

                                list.push(
                                    <tr
                                        key={`${queue.id}_${device.id}`}
                                        style={{
                                            display: opened ? 'table-row' : 'none',
                                        }}
                                        className={selectedTask && selectedTask.id === device.id ? styles.selected : ''}
                                        onClick={() => {
                                            onSelectTask(device)
                                        }}
                                    >
                                        <td className={cn(styles.td, styles.tdCheckbox)} />
                                        <td className={cn(styles.td, styles.tdArrow)} />
                                        <td className={styles.td} style={{ color }}>
                                            {getStatusName(device.task.status)}
                                        </td>
                                        <td className={styles.td}>{device.task.progress}%</td>
                                        <td className={styles.td} style={{ textAlign: 'left' }}>
                                            <div className={styles.iconAndName}>
                                                <Icon
                                                    containerClass={cn(grid.mr_micro, grid.pl_micro)}
                                                    type={'display'}
                                                    size={16}
                                                />
                                                {device.name}
                                            </div>
                                        </td>
                                        <td className={styles.td} />
                                        <td className={styles.td}>
                                            {moment(device.task.date).format('HH:mm:ss DD.MM.YYYY')}
                                        </td>
                                        <td className={styles.td}>{getLocalizedMessage(device.task.code)}</td>
                                    </tr>
                                )
                            })
                        }

                        return <>{list}</>
                    })}
                </tbody>
            </table>
        </div>
    )
}
